import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowPathIcon, BanknotesIcon } from '@heroicons/react/16/solid';
import { ErrorAlert } from '../common/Custom/ErrorAlert';
import { MoneyAttentionState } from './useMoneyAttention';
import { useUser } from '../hooks/UserContext';
import { UserType } from '../constants/UserTypes';

const plural = (n: number, one: string, many: string): string =>
  `${n} ${n === 1 ? one : many}`;

/**
 * Money that needs attention, per association (#670). Shown only to
 * someone allowed to see it; read once, not live, and labelled so.
 */
export const MoneyPanel: React.FC<{
  state: MoneyAttentionState;
  hoaName: (hoaId: string) => string;
}> = ({ state, hoaName }) => {
  const { rows, loading, error, asOf, reload } = state;
  const { userType } = useUser();
  const isSuperAdmin = userType === UserType.SUPER_ADMIN;
  return (
    <section
      aria-label="Money"
      className="flex min-w-0 flex-col overflow-hidden rounded-lg border border-line bg-raised"
    >
      <header className="flex items-center gap-2 border-b border-line/60 px-4 py-2.5">
        <h2 className="text-[13px] font-medium text-fg">Money</h2>
        <span className="text-[12px] text-fg-muted">
          {asOf
            ? `Not live · as of ${asOf.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}`
            : 'Not live'}
        </span>
        <button
          type="button"
          onClick={reload}
          disabled={loading}
          className="ml-auto inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[12px] text-fg-muted hover:bg-fg/5 hover:text-fg disabled:opacity-50"
        >
          <ArrowPathIcon className="h-3 w-3" aria-hidden />
          Refresh
        </button>
      </header>
      {error && (
        <ErrorAlert className="m-3" onRetry={reload}>
          {error}
        </ErrorAlert>
      )}
      {loading ? (
        <p className="px-4 py-8 text-center text-[13px] text-fg-muted">
          Loading…
        </p>
      ) : rows.length === 0 ? (
        !error && (
          <p className="px-4 py-8 text-center text-[13px] text-fg-muted">
            Nothing needs attention.
          </p>
        )
      ) : (
        <ul className="divide-y divide-line/50">
          {rows.map((r) => (
            <li
              key={r.hoaId}
              className="flex flex-wrap items-center gap-x-3 gap-y-1 px-4 py-2.5 text-[13px]"
            >
              <BanknotesIcon
                className="h-4 w-4 shrink-0 text-fg-muted"
                aria-hidden
              />
              <span className="min-w-0 flex-1 truncate text-fg">
                {hoaName(r.hoaId)}
              </span>
              {!!r.nsf &&
                (isSuperAdmin ? (
                  // The Banking page is scoped to the signed-in user's own
                  // company, so a super admin would land on an empty queue.
                  // Deciding an item is the company's job anyway.
                  <span
                    className="text-danger"
                    title="Decided by the company's Owner or Admin on their Banking page"
                  >
                    {plural(r.nsf, 'NSF item to decide', 'NSF items to decide')}
                  </span>
                ) : (
                  <Link to="/banking" className="text-danger hover:underline">
                    {plural(r.nsf, 'NSF item to decide', 'NSF items to decide')}
                  </Link>
                ))}
              {!!r.unmatched && (
                <Link
                  to={`/hoa/${r.hoaId}/billing/payments`}
                  className="text-warning hover:underline"
                >
                  {plural(
                    r.unmatched,
                    'unmatched payment',
                    'unmatched payments'
                  )}
                </Link>
              )}
              {!!r.review && (
                <Link
                  to={`/hoa/${r.hoaId}/billing`}
                  className="text-fg-muted hover:text-fg hover:underline"
                >
                  {plural(r.review, 'to review', 'to review')}
                </Link>
              )}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
};

export default MoneyPanel;
